import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, Plus, Send, Check, X, Package } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { pharmacyApi } from "@/api/pharmacy";
import {
  pharmacyPurchaseApi,
  type PharmacyPurchaseOrder,
  type PharmacyPurchaseOrderStatus,
} from "@/api/pharmacyPurchase";

type Drug = { id: string; name: string; strength?: string | null; form?: string | null };

const STATUS_LABEL: Record<PharmacyPurchaseOrderStatus, string> = {
  DRAFT: "Draft",
  SENT: "Sent to vendor",
  VENDOR_SUBMITTED: "Vendor submitted — verify delivery",
  PARTIALLY_ACCEPTED: "Partially received",
  ACCEPTED: "Accepted — in stock",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

function drugLabel(d?: Drug | null) {
  if (!d) return "—";
  return [d.name, d.strength, d.form].filter(Boolean).join(" · ");
}

type DeliveryLineForm = {
  batchNo: string;
  expiryDate: string;
  quantity: string;
  mrp: string;
  purchaseRate: string;
  gstRate: string;
  discountRate: string;
  notes: string;
};

type DeliveryVerifyForm = {
  invoiceNumber: string;
  invoiceDate: string;
  totalAmount: string;
  notes: string;
  lines: Record<string, DeliveryLineForm>;
};

function emptyDeliveryVerifyForm(): DeliveryVerifyForm {
  return { invoiceNumber: "", invoiceDate: "", totalAmount: "", notes: "", lines: {} };
}

function deliveryToVerifyForm(d: NonNullable<PharmacyPurchaseOrder["delivery"]>): DeliveryVerifyForm {
  const lines: Record<string, DeliveryLineForm> = {};
  for (const l of d.lines) {
    lines[l.id] = {
      batchNo: l.batchNo,
      expiryDate: l.expiryDate.slice(0, 10),
      quantity: String(l.quantity),
      mrp: String(l.mrp),
      purchaseRate: String(l.purchaseRate),
      gstRate: l.gstRate != null ? String(l.gstRate) : "",
      discountRate: l.discountRate != null ? String(l.discountRate) : "",
      notes: l.notes ?? "",
    };
  }
  return {
    invoiceNumber: d.invoiceNumber,
    invoiceDate: d.invoiceDate.slice(0, 10),
    totalAmount: String(d.totalAmount),
    notes: d.notes ?? "",
    lines,
  };
}

export default function PharmacyPurchaseOrdersPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [form, setForm] = useState({
    vendorId: "",
    notes: "",
    expectedDeliveryDate: "",
    lines: [{ drugId: "", orderedQty: "1", notes: "" }],
  });

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["pharmacy-purchase-orders"],
    queryFn: async () => {
      const res = await pharmacyPurchaseApi.list();
      return (res.data?.data ?? []) as PharmacyPurchaseOrder[];
    },
  });

  const selected = useMemo(
    () => orders.find((o) => o.id === selectedId) ?? null,
    [orders, selectedId]
  );

  const pendingAcceptLineIds = useMemo(
    () => selected?.delivery?.lines.filter((l) => !l.stockLedgerId).map((l) => l.id) ?? [],
    [selected]
  );

  const canVerifyDelivery =
    selected?.status === "VENDOR_SUBMITTED" || selected?.status === "PARTIALLY_ACCEPTED";

  const [checkedLineIds, setCheckedLineIds] = useState<string[]>([]);
  const [deliveryForm, setDeliveryForm] = useState<DeliveryVerifyForm>(emptyDeliveryVerifyForm);

  useEffect(() => {
    setCheckedLineIds(pendingAcceptLineIds);
  }, [selected?.id, pendingAcceptLineIds.join("|")]);

  useEffect(() => {
    if (selected?.delivery && canVerifyDelivery) {
      setDeliveryForm(deliveryToVerifyForm(selected.delivery));
    } else {
      setDeliveryForm(emptyDeliveryVerifyForm());
    }
  }, [selected?.id, selected?.delivery, canVerifyDelivery]);

  const { data: vendors = [] } = useQuery({
    queryKey: ["pharmacy-vendors"],
    queryFn: async () => {
      const res = await pharmacyApi.listVendors();
      return (res.data?.data ?? []) as Array<{ id: string; name: string; staffId?: string | null }>;
    },
  });

  const { data: drugs = [] } = useQuery({
    queryKey: ["prescription-drugs"],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>("/api/prescription/drugs");
      return res.data?.data ?? [];
    },
  });

  const createMut = useMutation({
    mutationFn: () =>
      pharmacyPurchaseApi.create({
        vendorId: form.vendorId,
        notes: form.notes || undefined,
        expectedDeliveryDate: form.expectedDeliveryDate || undefined,
        lines: form.lines
          .filter((l) => l.drugId && Number(l.orderedQty) > 0)
          .map((l) => ({
            drugId: l.drugId,
            orderedQty: Number(l.orderedQty),
            notes: l.notes || undefined,
          })),
      }),
    onSuccess: () => {
      toast.success("Purchase order created");
      setCreateOpen(false);
      setForm({ vendorId: "", notes: "", expectedDeliveryDate: "", lines: [{ drugId: "", orderedQty: "1", notes: "" }] });
      qc.invalidateQueries({ queryKey: ["pharmacy-purchase-orders"] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to create order"),
  });

  const sendMut = useMutation({
    mutationFn: (id: string) => pharmacyPurchaseApi.send(id),
    onSuccess: () => {
      toast.success("Order sent to vendor");
      qc.invalidateQueries({ queryKey: ["pharmacy-purchase-orders"] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to send"),
  });

  const buildAcceptPayload = (lineIds?: string[]) => {
    if (!selected?.delivery) return { lineIds };
    const pendingIds = pendingAcceptLineIds;
    const lineCorrections = pendingIds.map((id) => {
      const row = deliveryForm.lines[id];
      if (!row) throw new Error("Missing delivery line data");
      return {
        id,
        batchNo: row.batchNo.trim(),
        expiryDate: row.expiryDate,
        quantity: Number(row.quantity),
        mrp: Number(row.mrp),
        purchaseRate: Number(row.purchaseRate),
        gstRate: row.gstRate === "" ? null : Number(row.gstRate),
        discountRate: row.discountRate === "" ? null : Number(row.discountRate),
        notes: row.notes.trim() || null,
      };
    });
    const totalNum = Number(deliveryForm.totalAmount);
    return {
      lineIds,
      deliveryEdits: {
        invoiceNumber: deliveryForm.invoiceNumber.trim(),
        invoiceDate: deliveryForm.invoiceDate,
        totalAmount: totalNum > 0 ? totalNum : null,
        notes: deliveryForm.notes.trim() || null,
      },
      lineCorrections,
    };
  };

  const acceptMut = useMutation({
    mutationFn: ({ id, lineIds }: { id: string; lineIds?: string[] }) =>
      pharmacyPurchaseApi.accept(id, buildAcceptPayload(lineIds)),
    onSuccess: () => {
      toast.success("Verified — stock added for selected line(s)");
      qc.invalidateQueries({ queryKey: ["pharmacy-purchase-orders"] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to accept"),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      pharmacyPurchaseApi.reject(id, reason),
    onSuccess: () => {
      toast.success("Delivery rejected — vendor can resubmit");
      setRejectReason("");
      qc.invalidateQueries({ queryKey: ["pharmacy-purchase-orders"] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to reject"),
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => pharmacyPurchaseApi.cancel(id),
    onSuccess: () => {
      toast.success("Order cancelled");
      qc.invalidateQueries({ queryKey: ["pharmacy-purchase-orders"] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to cancel"),
  });

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Purchase orders</h1>
          <p className="text-sm text-muted-foreground">
            Send orders to vendors; verify delivery and auto stock-in on accept.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New order
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Orders</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : orders.length === 0 ? (
              <p className="text-sm text-muted-foreground">No purchase orders yet.</p>
            ) : (
              orders.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setSelectedId(o.id)}
                  className={`w-full rounded-lg border p-3 text-left transition hover:bg-muted/50 ${
                    selectedId === o.id ? "border-primary bg-muted/40" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{o.orderNumber}</p>
                      <p className="text-xs text-muted-foreground">{o.vendor?.name}</p>
                    </div>
                    <Badge variant="outline">{STATUS_LABEL[o.status]}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {format(new Date(o.createdAt), "dd MMM yyyy")} · {o.lines.length} item(s)
                  </p>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">Order detail</CardTitle>
          </CardHeader>
          <CardContent>
            {!selected ? (
              <p className="text-sm text-muted-foreground">Select an order to view details.</p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge>{STATUS_LABEL[selected.status]}</Badge>
                  <span className="text-sm text-muted-foreground">{selected.vendor?.name}</span>
                </div>
                {selected.notes && (
                  <p className="text-sm">
                    <span className="font-medium">Notes:</span> {selected.notes}
                  </p>
                )}

                <div>
                  <p className="mb-2 text-sm font-medium">Ordered items</p>
                  <ul className="space-y-1 text-sm">
                    {selected.lines.map((l) => (
                      <li key={l.id} className="flex justify-between gap-2 border-b py-1">
                        <span>{drugLabel(l.drug)}</span>
                        <span className="tabular-nums">Qty {l.orderedQty}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {selected.delivery && (
                  <div className="rounded-lg border bg-muted/20 p-3 space-y-3">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Package className="h-4 w-4" /> Vendor invoice
                    </p>
                    {canVerifyDelivery ? (
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Invoice number</Label>
                          <Input
                            value={deliveryForm.invoiceNumber}
                            onChange={(e) =>
                              setDeliveryForm((f) => ({ ...f, invoiceNumber: e.target.value }))
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Invoice date</Label>
                          <Input
                            type="date"
                            value={deliveryForm.invoiceDate}
                            onChange={(e) =>
                              setDeliveryForm((f) => ({ ...f, invoiceDate: e.target.value }))
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Invoice total (₹)</Label>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={deliveryForm.totalAmount}
                            onChange={(e) =>
                              setDeliveryForm((f) => ({ ...f, totalAmount: e.target.value }))
                            }
                          />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                          <Label className="text-xs">Invoice notes</Label>
                          <Input
                            value={deliveryForm.notes}
                            onChange={(e) =>
                              setDeliveryForm((f) => ({ ...f, notes: e.target.value }))
                            }
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm">
                        {selected.delivery.invoiceNumber} ·{" "}
                        {format(new Date(selected.delivery.invoiceDate), "dd MMM yyyy")} · ₹
                        {selected.delivery.totalAmount.toLocaleString("en-IN")}
                      </p>
                    )}
                    {selected.delivery.fileUrl && (
                      <a
                        href={selected.delivery.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary underline"
                      >
                        View uploaded invoice
                      </a>
                    )}
                    {canVerifyDelivery && pendingAcceptLineIds.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Compare with the physical invoice and correct batch, expiry, qty, and rates
                        before accept. Select line(s) to stock in when goods arrive; lines already
                        accepted are read-only.
                      </p>
                    )}
                    <ul className="space-y-3 text-sm">
                      {selected.delivery.lines.map((l) => {
                        const inStock = Boolean(l.stockLedgerId);
                        const pending = !inStock && canVerifyDelivery;
                        const checked = checkedLineIds.includes(l.id);
                        const row = deliveryForm.lines[l.id];
                        const patchLine = (patch: Partial<DeliveryLineForm>) => {
                          setDeliveryForm((f) => ({
                            ...f,
                            lines: {
                              ...f.lines,
                              [l.id]: { ...f.lines[l.id], ...patch },
                            },
                          }));
                        };
                        return (
                          <li key={l.id} className="border-t pt-3 flex gap-2 items-start">
                            {pending ? (
                              <Checkbox
                                className="mt-2"
                                checked={checked}
                                onCheckedChange={(v) => {
                                  setCheckedLineIds((prev) =>
                                    v
                                      ? [...prev, l.id]
                                      : prev.filter((id) => id !== l.id)
                                  );
                                }}
                              />
                            ) : (
                              <span className="w-4 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0 space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">{drugLabel(l.drug)}</span>
                                {inStock ? (
                                  <Badge variant="secondary" className="text-[10px]">
                                    In stock
                                  </Badge>
                                ) : null}
                              </div>
                              {pending && row ? (
                                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Batch</Label>
                                    <Input
                                      value={row.batchNo}
                                      onChange={(e) => patchLine({ batchNo: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Expiry</Label>
                                    <Input
                                      type="date"
                                      value={row.expiryDate}
                                      onChange={(e) => patchLine({ expiryDate: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Qty</Label>
                                    <Input
                                      type="number"
                                      min={1}
                                      value={row.quantity}
                                      onChange={(e) => patchLine({ quantity: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">MRP (₹)</Label>
                                    <Input
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      value={row.mrp}
                                      onChange={(e) => patchLine({ mrp: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Purchase rate (₹)</Label>
                                    <Input
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      value={row.purchaseRate}
                                      onChange={(e) => patchLine({ purchaseRate: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">GST %</Label>
                                    <Input
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      value={row.gstRate}
                                      onChange={(e) => patchLine({ gstRate: e.target.value })}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <p className="text-muted-foreground text-xs">
                                  Batch {l.batchNo} · Exp{" "}
                                  {format(new Date(l.expiryDate), "dd MMM yyyy")} · Qty {l.quantity}{" "}
                                  · MRP ₹{l.mrp} · Rate ₹{l.purchaseRate}
                                </p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {selected.rejectionReason && (
                  <p className="text-sm text-red-600">Rejected: {selected.rejectionReason}</p>
                )}

                <div className="flex flex-wrap gap-2 pt-2">
                  {selected.status === "DRAFT" && (
                    <>
                      <Button size="sm" onClick={() => sendMut.mutate(selected.id)} disabled={sendMut.isPending}>
                        <Send className="mr-1 h-4 w-4" /> Send to vendor
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancelMut.mutate(selected.id)}
                        disabled={cancelMut.isPending}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {canVerifyDelivery && pendingAcceptLineIds.length > 0 && (
                    <Button
                      size="sm"
                      onClick={() =>
                        acceptMut.mutate({
                          id: selected.id,
                          lineIds:
                            checkedLineIds.length > 0 &&
                            checkedLineIds.length < pendingAcceptLineIds.length
                              ? checkedLineIds.filter((id) => pendingAcceptLineIds.includes(id))
                              : undefined,
                        })
                      }
                      disabled={
                        acceptMut.isPending ||
                        (checkedLineIds.length > 0 &&
                          !checkedLineIds.some((id) => pendingAcceptLineIds.includes(id)))
                      }
                    >
                      <Check className="mr-1 h-4 w-4" />
                      {checkedLineIds.length > 0 &&
                      checkedLineIds.length < pendingAcceptLineIds.length
                        ? `Accept ${checkedLineIds.filter((id) => pendingAcceptLineIds.includes(id)).length} selected`
                        : "Accept all pending & stock in"}
                    </Button>
                  )}
                  {selected.status === "VENDOR_SUBMITTED" && (
                    <div className="flex flex-1 min-w-[200px] gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-xs">Reject reason</Label>
                        <Input
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Optional reason for vendor"
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => rejectMut.mutate({ id: selected.id, reason: rejectReason })}
                        disabled={rejectMut.isPending}
                      >
                        <X className="mr-1 h-4 w-4" /> Reject delivery
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New purchase order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vendor</Label>
              <Select value={form.vendorId} onValueChange={(v) => setForm((f) => ({ ...f, vendorId: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                      {!v.staffId ? " (no portal login)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Expected delivery</Label>
                <Input
                  type="date"
                  value={form.expectedDeliveryDate}
                  onChange={(e) => setForm((f) => ({ ...f, expectedDeliveryDate: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Line items</Label>
              {form.lines.map((line, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-7">
                    <Select
                      value={line.drugId}
                      onValueChange={(v) =>
                        setForm((f) => {
                          const lines = [...f.lines];
                          lines[idx] = { ...lines[idx], drugId: v };
                          return { ...f, lines };
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Drug" />
                      </SelectTrigger>
                      <SelectContent>
                        {drugs.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {drugLabel(d)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min={1}
                      placeholder="Qty"
                      value={line.orderedQty}
                      onChange={(e) =>
                        setForm((f) => {
                          const lines = [...f.lines];
                          lines[idx] = { ...lines[idx], orderedQty: e.target.value };
                          return { ...f, lines };
                        })
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={form.lines.length <= 1}
                      onClick={() =>
                        setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))
                      }
                    >
                      ×
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    lines: [...f.lines, { drugId: "", orderedQty: "1", notes: "" }],
                  }))
                }
              >
                Add line
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMut.mutate()}
              disabled={!form.vendorId || createMut.isPending}
            >
              {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
